import { useEffect, useState } from 'react';

/**
 * Zdarzenie `beforeinstallprompt` (Android/Chrome i desktopowe przeglądarki oparte na
 * Chromium) — przeglądarka nie definiuje go w standardowych typach DOM, bo nie jest
 * jeszcze ustandaryzowane (tylko projekt specyfikacji).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  // iOS Safari nie zna `display-mode`, ale ma własne, niestandardowe pole na `navigator`.
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * Stan modułowy, nie stan komponentu — celowo. `beforeinstallprompt` potrafi odpalić się
 * ZANIM React zdąży się zamontować i podpiąć nasłuch w `useEffect` (typowo widoczne po
 * twardym odświeżeniu na telefonie: strona ładuje się, zdarzenie leci niemal natychmiast,
 * a komponent jeszcze nie istnieje) — wtedy `useEffect`-owy listener już go nie złapie
 * i przycisk instalacji nigdy się nie pojawia, mimo że przeglądarka i tak uznała stronę
 * za instalowalną. Nasłuch na poziomie modułu odpala się w chwili wykonania tego pliku
 * (czyli przy starcie skryptu, dużo wcześniej niż jakikolwiek `useEffect`), więc łapie
 * zdarzenie niezależnie od tego, kiedy komponent faktycznie się zamontuje.
 */
let capturedEvent: BeforeInstallPromptEvent | null = null;
let installed = isStandalone();
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    capturedEvent = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    capturedEvent = null;
    notify();
  });
}

/**
 * Instalacja PWA (`docs/05-ux-styl-szkicu.md` §5.6) — trzy różne drogi w zależności od
 * przeglądarki, bo nie ma tu jednego wspólnego API:
 *  - Android/Chrome i desktop Chromium: przeglądarka sama wysyła `beforeinstallprompt`,
 *    który łapiemy na poziomie modułu (patrz wyżej) i trzymamy do wywołania na życzenie
 *    (stąd `canInstall`/`promptInstall`);
 *  - iOS Safari: nie wysyła tego zdarzenia w ogóle (Apple świadomie go nie zaimplementował)
 *    — jedyna droga to ręczna instrukcja "Udostępnij → Dodaj do ekranu początkowego",
 *    stąd `isIos` osobno, żeby UI mogło pokazać instrukcję zamiast przycisku;
 *  - już zainstalowana (dowolna przeglądarka): `installed` pozwala UI nic nie pokazywać.
 */
export function useInstallPrompt() {
  const [, forceRerender] = useState(0);

  useEffect(() => {
    const listener = () => forceRerender((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  async function promptInstall(): Promise<void> {
    if (!capturedEvent) return;
    await capturedEvent.prompt();
    await capturedEvent.userChoice;
    // Zdarzenie da się zużyć tylko raz — po decyzji (w każdą stronę) nie ma go czym powtórzyć.
    capturedEvent = null;
    notify();
  }

  return {
    installed,
    canInstall: capturedEvent !== null,
    promptInstall,
    isIos: /iphone|ipad|ipod/i.test(navigator.userAgent),
  };
}
